/**
 * API Factory - Auto-generate CRUD endpoints
 *
 * Automatically creates standard API methods (fetchAll, fetchOne, create, update, delete)
 * with pagination support, just by providing a base endpoint slug.
 *
 * Used internally by createRepository, but can also be used standalone.
 *
 * Usage:
 * const api = createAPI('admin/enterprise-subscriptions');
 *
 * Generates:
 * - fetchAll({ page, perPage, filters })  -> GET /api/admin/enterprise-subscriptions
 * - fetchOne({ id })                      -> GET /api/admin/enterprise-subscriptions/:id
 * - create({ data })                      -> POST /api/admin/enterprise-subscriptions
 * - update({ id, data })                  -> PATCH /api/admin/enterprise-subscriptions/:id
 * - delete({ id })                        -> DELETE /api/admin/enterprise-subscriptions/:id
 */

import { getApiUrl } from 'data/services/apiConstants';
import { get, post, put, del } from 'data/services/apiService';

/**
 * Build URL with query parameters
 */
const buildURL = (baseURL, params = {}) => {
	const url = new URL(baseURL);
	Object.keys(params).forEach((key) => {
		if (params[key] !== null && params[key] !== undefined) {
			url.searchParams.append(key, params[key]);
		}
	});
	return url.toString();
};

const getBaseUrl = (endpoint) => {
	return `${getApiUrl()}/api/${endpoint}`;
};

/**
 * Create standard API methods for a resource
 *
 * @param {string} endpoint - API endpoint slug (e.g., 'admin/enterprise-subscriptions')
 * @param {Object} options - Configuration options
 * @param {Object} options.customMethods - Custom API methods to add
 * @param {string} options.updateMethod - HTTP method for updates ('patch' | 'put' | 'post')
 * @param {string} options.deleteMethod - HTTP method for delete ('delete' | 'post')
 * @param {boolean} options.pagination - Enable pagination support (default: true)
 * @param {Object} options.defaultParams - Default query parameters
 * @returns {Object} API methods object
 */
export const createAPI = (endpoint, options = {}) => {
	const {
		customMethods = {},
		updateMethod = 'patch',
		deleteMethod = 'delete',
		pagination = true,
		defaultParams = {},
	} = options;

	const baseURL = getBaseUrl(endpoint);

	// HTTP method mapping
	const httpMethods = {
		patch: put,
		put,
		post,
		delete: del,
	};

	const api = {
		/**
		 * Fetch all items (with pagination and filtering)
		 */
		fetchAll: ({ authToken, dispatch, page, perPage, ...queryParams }) => {
			const params = { ...defaultParams, ...queryParams };

			if (pagination) {
				if (page) params.page = page;
				if (perPage) params.per_page = perPage;
			}

			const url = buildURL(getBaseUrl(endpoint), params);
			return get({ url, authToken, dispatch });
		},

		/**
		 * Fetch single item by ID
		 */
		fetchOne: ({ id, authToken, dispatch, ...queryParams }) => {
			const params = { ...defaultParams, ...queryParams };
			const url = buildURL(`${baseURL}/${id}`, params);
			return get({ url, authToken, dispatch });
		},

		/**
		 * Create new item
		 */
		create: ({ data, authToken, dispatch }) => {
			return post({ url: getBaseUrl(endpoint), data, authToken, dispatch });
		},

		/**
		 * Update existing item
		 */
		update: ({ id, data, authToken, dispatch }) => {
			const method = httpMethods[updateMethod] || put;
			return method({
				url: `${getBaseUrl(endpoint)}/${id}`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Delete item
		 */
		delete: ({ id, authToken, dispatch }) => {
			const method = httpMethods[deleteMethod] || del;

			if (deleteMethod === 'post') {
				return method({
					url: `${baseURL}/${id}`,
					data: {},
					authToken,
					dispatch,
				});
			}

			return method({
				url: `${getBaseUrl(endpoint)}/${id}`,
				authToken,
				dispatch,
			});
		},

		// Add custom methods
		...customMethods,
	};

	return api;
};

export default createAPI;
